import { buildSaldoInicialEntry } from "./saldoInicial";

// ─── Tolerancia de redondeo ──────────────────────────────────────────────────
// Una factura que, TRAS recibir pago (o NC/MBF), queda con una miga de saldo ≤ este umbral se
// considera saldada al 100% y sale de "Por pagar"/"parcial". Caso real: 3 pagos de 388.733
// (=1.166.199) contra una factura de 1.166.200 → residuo de $1 (INNOVA folio 2698).
// Guarda IMPRESCINDIBLE: SOLO se aplica si hubo pago (débito − saldo > 0). Una factura nunca
// pagada (saldo == débito) NO se barre, por chica que sea. El monto_total real de la factura
// NO se toca (no se falsifica plata): solo deja de proyectarse la miga como pendiente.
// El umbral está en MONEDA LOCAL del país (CLP/PEN). El valor efectivo por-ledger se deriva con
// toDisplayCurrency(TOLERANCIA_REDONDEO_LOCAL, monedaPrincipal): en un ledger expresado en moneda
// local vale ~10; en un ledger USD colapsa a ~0 → inerte. NOTA: un residuo genuino en USD
// necesitaría su propio umbral EN USD — fuera de alcance hoy (no hay residuos USD sub-umbral).
export const TOLERANCIA_REDONDEO_LOCAL = 10;

/**
 * Construye el ledger contable de un proveedor (movimientos + saldo acumulado).
 *
 * Convención de signo del saldo acumulado:
 *   - saldo > 0  → el proveedor nos debe (saldo a nuestro favor)
 *   - saldo < 0  → le debemos al proveedor (deuda nuestra)
 *
 * Débitos (suben el saldo a favor nuestro):
 *   - Facturas SII recibidas (origen "SII"). NC (tipo_dte=61) entra como CRÉDITO POSITIVO
 *     (bucket "nota_credito"), no como débito negativo — reduce el saldo igual pero contable
 *     y visualmente queda en el haber. Ver regla 10.42.
 *   - Facturas externas no anuladas (origen "EXT") — moneda nativa.
 *   - Cobros a cuenta del proveedor — `origen: null` (importante para el filtro de
 *     "Total facturado" en EstadoCuenta, que excluye este origen).
 *     movimientos_caja tipo='cobro' entidad_tipo='proveedor'. **Son débitos** porque
 *     representan transferencias entrantes DESDE el proveedor HACIA la empresa
 *     (préstamos del socio, devoluciones de anticipos previos, refacturaciones).
 *     Aumentan el saldo a favor nuestro: si previamente pagamos de más al proveedor
 *     (saldo<0), una devolución suya cancela ese sobrepago. Si no había deuda
 *     previa, este cobro se convierte en saldo positivo (el proveedor nos quedó
 *     debiendo esa plata). NUNCA confundir con "el proveedor está pagando una
 *     deuda con nosotros" — eso no aplica en una cuenta corriente de proveedor.
 *
 * Créditos (suben la deuda con el proveedor):
 *   - Pagos al proveedor: movimientos_caja tipo='pago' entidad_tipo='proveedor'.
 *     Concepto = "Pago a cuenta" si categoria_pago='pago_a_cuenta', si no "Pago".
 *
 * Saldo inicial opcional al 2026-01-01 entra como entry sintética (origen "INI").
 *
 * Filtrado por entidad: SIEMPRE por proveedor_id numérico, nunca por nombre.
 *
 * `saldosPorFactura` se construye en el mismo loop aplicando FIFO de pagos
 * (entries con `origen===null` y `creditoDisplay>0`) sobre las facturas SII/EXT
 * registradas hasta ese punto cronológico. Una factura cuyo saldoDisplay queda ≤ 0.01
 * se filtra al final. NCs (debito=0, credito>0) no se registran. Cobros a cuenta y saldo
 * inicial no participan ni de la registración ni de la aplicación FIFO.
 *
 * @param {object} args
 * @param {number} args.provId                    ID numérico del proveedor.
 * @param {Array}  args.siiRecibidas              Facturas SII recibidas del país.
 * @param {Array}  args.facturasExternas          Facturas externas del país.
 * @param {Array}  args.movimientosCaja           Movimientos de caja del país desde FECHA_CORTE.
 * @param {object} [args.saldoInicial]            Saldo inicial de la entidad (o null).
 * @param {string} args.monedaPrincipal           Moneda local del país ("CLP" o "PEN").
 * @param {Function} args.toDisplayCurrency       (monto, monedaOrigen, fecha) → monto en moneda display.
 * @param {Array}  [args.vinculacionesDirectas]   Filas de movimiento_bancario_facturas (MBF), sin filtrar
 *   por proveedor. Una factura con vinculación directa a un pago vía MBF se considera 100% saldada
 *   (no entra a saldosPorFactura), y el pago vinculado NO participa del FIFO. Evita residuos fantasma
 *   por drift de TC cuando un pago en moneda local salda 1:1 una factura en otra moneda. Default: [].
 * @param {Set|Array} [args.facturasComisionFactoring] IDs de facturas SII recibidas que son comisión de
 *   factoring vinculada a una operación (se descuentan del giro, no son deuda real). Se excluyen del
 *   ledger y del FIFO. Default: Set vacío.
 * @returns {{ entries: Array, saldosPorFactura: Map<string,{saldoDisplay,factura}> }}
 *   - `entries`: ordenadas DESC por fecha, con debitoDisplay/creditoDisplay/saldo.
 *   - `saldosPorFactura`: keyed por `"${origen}:${facturaId}"` — composite porque SII/EXT
 *     son tablas con IDs independientes que pueden colisionar en raw id. Solo facturas con
 *     saldoDisplay > 0.01 (deuda restante). `saldoDisplay` está en la moneda que produzca
 *     `toDisplayCurrency` (el consumidor sabe qué moneda pasó).
 */
export const construirLedgerProveedor = ({
  provId,
  siiRecibidas,
  facturasExternas,
  movimientosCaja,
  saldoInicial,
  monedaPrincipal,
  toDisplayCurrency,
  vinculacionesDirectas = [],
  excluirProvIds = new Set(),
  facturasComisionFactoring = new Set(),
}) => {
  if (!provId) return [];
  // Comisiones de factoring (Xepelin/X Capital, Chita, Finto/Santander) vinculadas a una
  // operación: se descuentan del giro en origen, NO son deuda real. Se excluyen del ledger
  // (no entran como débito ni al FIFO). Acepta Set o array. Ver operaciones_factoring_comisiones.
  const setComisionFactoring =
    facturasComisionFactoring instanceof Set ? facturasComisionFactoring : new Set(facturasComisionFactoring || []);
  // Proveedor-edificio (su id está en edificios.proveedor_id): su deuda se mide solo por la
  // comisión de sus edificios (getEdificiosSaldoPendiente), no como obligación de proveedor.
  // Excluirlo acá evita el doble conteo en TODO consumidor del ledger (FlujoCajaV2, EECC).
  // Comparación por ID (regla 8). Acepta Set o array.
  const setExcluir = excluirProvIds instanceof Set ? excluirProvIds : new Set(excluirProvIds || []);
  if (setExcluir.has(provId)) return [];

  const debitosSii = siiRecibidas
    .filter((f) => f.proveedor_id === provId && !setComisionFactoring.has(f.id))
    .map((f) => ({
      fecha: f.fecha_emision,
      concepto: f.tipo_dte === 61 ? "Nota de crédito" : "Factura",
      origen: "SII",
      referencia: f.folio ? String(f.folio) : "-",
      facturaId: f.id,
      // NC (tipo_dte=61) entra como CRÉDITO POSITIVO (no débito negativo): contablemente
      // reduce la deuda y va en su propio bucket "nota_credito". El saldo corrido queda
      // idéntico (Pasada 1: 0 - X == -X - 0). Sigue fuera del FIFO (Pasada 2 exige
      // debitoDisplay>0; Pasada 3 atrapa por origen!==null). Ver regla 10.42.
      debito: f.tipo_dte === 61 ? 0 : Number(f.monto_total),
      credito: f.tipo_dte === 61 ? Number(f.monto_total) : 0,
      moneda: monedaPrincipal,
      bucket: f.tipo_dte === 61 ? "nota_credito" : "factura",
    }));

  const debitosExt = facturasExternas
    .filter((f) => f.proveedor_id === provId && f.estado !== "anulada")
    .map((f) => ({
      fecha: f.fecha_emision,
      concepto: f.concepto || null,
      origen: "EXT",
      referencia: f.numero_documento || "-",
      facturaId: f.id,
      debito: Number(f.monto_total),
      credito: 0,
      moneda: f.moneda || monedaPrincipal,
      bucket: "factura",
      archivo_url: f.archivo_url || null,
      periodo_mes: f.periodo_mes || null,
    }));

  // Cobros a cuenta corriente del proveedor (devolución, anticipo retornado, refacturación):
  // movimientos tipo='cobro' con entidad_tipo='proveedor'. Aumentan el saldo a favor del
  // proveedor — se contabilizan como débito en el ledger.
  const debitosCobroAcuenta = movimientosCaja
    .filter((m) => m.tipo === "cobro" && m.entidad_tipo === "proveedor" && m.entidad_id === provId)
    .map((m) => ({
      fecha: m.fecha,
      concepto: m.descripcion || "Cobro a cuenta",
      origen: null,
      referencia: m.numero_documento || "-",
      descripcion: m.descripcion || "",
      debito: Number(m.monto),
      credito: 0,
      moneda: m.moneda || monedaPrincipal,
      bucket: "credito",
      // Datos para reclasificar el cobro desde EstadoCuenta (columna TIPO PAGO). El bucket
      // sigue siendo "credito" (cuenta corriente); solo se reclasifica categoria_pago.
      cajaId: m.id,
      categoriaPago: m.categoria_pago,
    }));

  const creditos = movimientosCaja
    .filter((m) => m.tipo === "pago" && m.entidad_tipo === "proveedor" && m.entidad_id === provId)
    .map((m) => ({
      fecha: m.fecha,
      concepto: m.categoria_pago === "pago_a_cuenta" ? "Pago a cuenta" : "Pago",
      origen: null,
      referencia: m.numero_documento || "-",
      descripcion: m.descripcion || "",
      debito: 0,
      credito: Number(m.monto),
      moneda: m.moneda || monedaPrincipal,
      bucket: m.categoria_pago === "pago_credito" || m.categoria_pago === "pago_a_cuenta" ? "credito" : "factura",
      // Para detectar pagos con vinculación directa a factura (MBF) y excluirlos del FIFO.
      movimientoBancarioId: m.movimiento_bancario_id ?? null,
      // Datos para que EstadoCuenta pueda accionar sobre el pago (desvincular/eliminar).
      cajaId: m.id,
      categoriaPago: m.categoria_pago,
      tieneMBF: false, // se sobreescribe abajo si el pago tiene fila MBF
    }));

  const inicial = saldoInicial ? [{ ...buildSaldoInicialEntry(saldoInicial, monedaPrincipal), bucket: "credito" }] : [];
  const entries = [...inicial, ...debitosSii, ...debitosExt, ...debitosCobroAcuenta, ...creditos].sort((a, b) =>
    a.fecha.localeCompare(b.fecha)
  );

  // Vinculaciones directas (MBF) con montos parciales: una factura puede ser saldada por N
  // movimientos bancarios (caso Santander: débito CLP + débito USD contra la misma factura).
  // Para cada factura se acumula el monto vinculado EN MONEDA DISPLAY — cada mbf.monto está en
  // la moneda nativa de su movimiento (Fase 1 guarda Math.abs(movimiento.monto)), así que se
  // convierte con la moneda/fecha del pago. Si Σ vinculado ≥ débito → saldada al 100% (fuera
  // de "Por pagar"); si es parcial → solo el remanente entra a la cola FIFO. El crédito de cada
  // pago se fuerza a su mbf.monto (no al total de la factura) para que el split acredite el
  // parcial real. Las keys son composite "ORIGEN:facturaId"; MBF mapea sii_recibida→SII,
  // factura_externa→EXT (sii_emitida es de clientes, no aplica al ledger de proveedor).
  const montosVinculadosPorFactura = new Map(); // "ORIGEN:facturaId" → Σ mbf.monto (en display)
  const montoPorPagoMBF = new Map(); // movimiento_bancario_id → Σ mbf.monto del pago (moneda nativa)
  const pagosConsumidosPorMBF = new Set();
  // Para el neteo exacto del drift cambiario (Pasada 1): pago → facturas que cubre, cuántos pagos
  // distintos tocan cada factura (para excluir el split N-movimientos→1-factura) y el débito display
  // de cada factura.
  const facturasPorPago = new Map(); // movimiento_bancario_id → Set de "ORIGEN:facturaId"
  const pagosPorFacturaSet = new Map(); // "ORIGEN:facturaId" → Set de movimiento_bancario_id
  const debitoDisplayPorFactura = new Map(); // "ORIGEN:facturaId" → debitoDisplay
  const monedaPorFactura = new Map(); // "ORIGEN:facturaId" → moneda nativa de la factura

  // Keys de facturas de ESTE proveedor (para ignorar filas MBF que apunten a facturas de otro
  // proveedor o a sii_emitida — mismo criterio defensivo que el código previo). De paso se guarda
  // el débito display y la moneda de cada factura (reusados por el neteo exacto de la Pasada 1).
  const facturaKeysProveedor = new Set();
  for (const e of [...debitosSii, ...debitosExt]) {
    if (e.facturaId != null && e.debito > 0) {
      const key = `${e.origen}:${e.facturaId}`;
      facturaKeysProveedor.add(key);
      debitoDisplayPorFactura.set(key, toDisplayCurrency(e.debito, e.moneda, e.fecha));
      monedaPorFactura.set(key, e.moneda);
    }
  }
  // Moneda + fecha de cada pago bancario del proveedor (para convertir su mbf.monto a display
  // de forma consistente entre el lado factura y el lado pago).
  const infoPagoPorMov = new Map();
  for (const c of creditos) {
    if (c.movimientoBancarioId != null)
      infoPagoPorMov.set(c.movimientoBancarioId, { moneda: c.moneda, fecha: c.fecha });
  }
  for (const mbf of vinculacionesDirectas) {
    const origenKey =
      mbf.factura_tipo === "sii_recibida" ? "SII" : mbf.factura_tipo === "factura_externa" ? "EXT" : null;
    if (origenKey && mbf.factura_id != null) {
      const key = `${origenKey}:${mbf.factura_id}`;
      if (facturaKeysProveedor.has(key)) {
        const info = infoPagoPorMov.get(mbf.movimiento_bancario_id);
        const montoDisplay = toDisplayCurrency(
          Number(mbf.monto || 0),
          info?.moneda || monedaPrincipal,
          info?.fecha || null
        );
        montosVinculadosPorFactura.set(key, (montosVinculadosPorFactura.get(key) || 0) + montoDisplay);
        if (mbf.movimiento_bancario_id != null) {
          montoPorPagoMBF.set(
            mbf.movimiento_bancario_id,
            (montoPorPagoMBF.get(mbf.movimiento_bancario_id) || 0) + Number(mbf.monto || 0)
          );
          if (!facturasPorPago.has(mbf.movimiento_bancario_id))
            facturasPorPago.set(mbf.movimiento_bancario_id, new Set());
          facturasPorPago.get(mbf.movimiento_bancario_id).add(key);
          if (!pagosPorFacturaSet.has(key)) pagosPorFacturaSet.set(key, new Set());
          pagosPorFacturaSet.get(key).add(mbf.movimiento_bancario_id);
        }
      }
    }
    if (mbf.movimiento_bancario_id != null) {
      pagosConsumidosPorMBF.add(mbf.movimiento_bancario_id);
    }
  }

  // Pasada 1 — enrich con saldo acumulado. Sin lógica FIFO adentro. Para un pago con
  // vinculación directa (MBF), el creditoDisplay se fuerza a la suma de los debitoDisplay de
  // las facturas que cancela, de modo que se anulen exacto contra los débitos de esas facturas.
  let saldo = 0;
  const entriesConSaldo = entries.map((entry) => {
    const debitoDisplay = toDisplayCurrency(entry.debito, entry.moneda, entry.fecha);
    let creditoDisplay = toDisplayCurrency(entry.credito, entry.moneda, entry.fecha);
    if (
      entry.origen === null &&
      entry.credito > 0 &&
      entry.movimientoBancarioId != null &&
      pagosConsumidosPorMBF.has(entry.movimientoBancarioId)
    ) {
      // Neteo exacto del drift cambiario. Un pago es "coverer exacto" si TODAS las facturas que
      // toca cumplen: (a) es CROSS-CURRENCY (moneda del pago ≠ moneda de la factura) — el drift solo
      // existe al convertir de una moneda a otra; (b) es el ÚNICO pago que las cubre (no split
      // N-movimientos→1-factura); (c) están 100% cubiertas. En ese caso el crédito se fuerza a la Σ
      // de los débitos display de esas facturas → débito y crédito se anulan EXACTO, sin arrastrar la
      // diferencia de TC (CLP→USD con Math.round). Si no, se mantiene la conversión del pago
      // (Σ mbf.monto convertido) para que el remanente/diferencia real siga contando. El gate (a)
      // garantiza que en MISMA moneda (OnSign USD, proveedor CLP) es NO-OP: no se toca ningún
      // sobrepago/diferencia same-currency real.
      const mbfMonto = montoPorPagoMBF.get(entry.movimientoBancarioId);
      if (mbfMonto != null) {
        const facturasCubiertas = facturasPorPago.get(entry.movimientoBancarioId);
        let covererExacto = facturasCubiertas != null && facturasCubiertas.size > 0;
        let sumaDebitoCubierto = 0;
        if (covererExacto) {
          for (const fkey of facturasCubiertas) {
            const debFactura = debitoDisplayPorFactura.get(fkey) || 0;
            const esCrossCurrency = monedaPorFactura.get(fkey) !== entry.moneda;
            const unicoPago = (pagosPorFacturaSet.get(fkey)?.size || 0) === 1;
            const cubierta100 = (montosVinculadosPorFactura.get(fkey) || 0) >= debFactura - 0.01;
            if (!esCrossCurrency || !unicoPago || !cubierta100) {
              covererExacto = false;
              break;
            }
            sumaDebitoCubierto += debFactura;
          }
        }
        creditoDisplay = covererExacto ? sumaDebitoCubierto : toDisplayCurrency(mbfMonto, entry.moneda, entry.fecha);
      }
    }
    saldo += debitoDisplay - creditoDisplay;
    return { ...entry, debitoDisplay, creditoDisplay, saldo };
  });

  // Pasada 2 — registrar TODAS las facturas SII/EXT primero (ASC por fecha por el
  // sort previo). Separarlo de la aplicación FIFO evita el bug del cursor cuando un
  // pago ocurre cronológicamente ANTES de algunas facturas: en single-pass `fifoIdx`
  // avanzaría sobre una cola incompleta y las facturas posteriores quedarían sin
  // recibir crédito remanente. Composite key porque SII/EXT tienen IDs
  // independientes que pueden colisionar dentro del mismo proveedor.
  const saldosPorFactura = new Map();
  const colaFacturas = [];
  // Contadores para decidir si TODA la deuda del proveedor está cubierta por MBF (→ residuo 0).
  let totalFacturasProveedor = 0;
  let facturasExcluidasMBF = 0;
  for (const entry of entriesConSaldo) {
    const esFactura =
      (entry.origen === "SII" || entry.origen === "EXT") && entry.debitoDisplay > 0 && entry.facturaId != null;
    // Saldo inicial (origen "INI", débito > 0) entra a la cola como la obligación MÁS ANTIGUA
    // (fecha = FECHA_CORTE, primera del sort) → los pagos/NCs amortizan primero la deuda
    // pre-2026 y las facturas 2026 conservan su saldo real (evita residuo fantasma). No es
    // "factura" (no cuenta para totalFacturasProveedor) y nunca tiene MBF.
    const esSaldoIni = entry.origen === "INI" && entry.debitoDisplay > 0;
    if (!esFactura && !esSaldoIni) continue;
    const key = esSaldoIni ? `INI:${provId}` : `${entry.origen}:${entry.facturaId}`;
    if (esFactura) {
      totalFacturasProveedor++;
      // Vinculación directa por montos (MBF): comparar la suma vinculada (en display) contra el
      // débito de la factura. Cubre el total → saldada al 100% (fuera de "Por pagar", pago(s)
      // excluido(s) del FIFO en la Pasada 3). Parcial → solo el remanente entra a la cola FIFO.
      // Soporta N movimientos → 1 factura (incluido el split cross-currency CLP + USD).
      const montoVinculadoDisplay = montosVinculadosPorFactura.get(key) || 0;
      if (montoVinculadoDisplay > 0.01) {
        if (montoVinculadoDisplay >= entry.debitoDisplay - 0.01) {
          facturasExcluidasMBF++;
          continue;
        }
        saldosPorFactura.set(key, { saldoDisplay: entry.debitoDisplay - montoVinculadoDisplay, factura: entry });
        colaFacturas.push(key);
        continue;
      }
    }
    saldosPorFactura.set(key, { saldoDisplay: entry.debitoDisplay, factura: entry });
    colaFacturas.push(key);
  }

  // Pasada 3 — aplicar reducciones FIFO sobre la cola completa (oldest-first):
  //   (a) PAGOS al proveedor: origen=null + creditoDisplay>0 + bucket!="credito".
  //   (b) NOTAS DE CRÉDITO: bucket="nota_credito" + creditoDisplay>0 — reducen la deuda
  //       contra las obligaciones más antiguas igual que un pago. Antes quedaban FUERA del
  //       FIFO (solo bajaban el saldo global) y generaban residuo fantasma; ahora se
  //       distribuyen sobre la cola (incluido el saldo inicial, que es el ítem más antiguo).
  // NO participan: cobros a cuenta (origen=null + debito>0) ni pagos bucket crédito
  // (pago_credito/pago_a_cuenta) — netean contra sus pares en el saldo global. El saldo
  // inicial participa como ITEM de la cola (Pasada 2), no como reducción.
  let fifoIdx = 0;
  for (const entry of entriesConSaldo) {
    const esPagoFifo = entry.origen === null && entry.creditoDisplay > 0.01 && entry.bucket !== "credito";
    const esNotaCredito = entry.bucket === "nota_credito" && entry.creditoDisplay > 0.01;
    if (!esPagoFifo && !esNotaCredito) continue;
    // Pago con vinculación directa (MBF): ya saldó su(s) factura(s) explícitamente, no se
    // reparte por FIFO (si no, comería saldo de otras facturas y dejaría residuos fantasma).
    if (esPagoFifo && entry.movimientoBancarioId != null && pagosConsumidosPorMBF.has(entry.movimientoBancarioId))
      continue;
    let restante = entry.creditoDisplay;
    while (restante > 0.01 && fifoIdx < colaFacturas.length) {
      const item = saldosPorFactura.get(colaFacturas[fifoIdx]);
      if (!item || item.saldoDisplay <= 0.01) {
        fifoIdx++;
        continue;
      }
      const aplicar = Math.min(restante, item.saldoDisplay);
      item.saldoDisplay -= aplicar;
      restante -= aplicar;
      if (item.saldoDisplay <= 0.01) fifoIdx++;
    }
  }

  // Descartar facturas saldadas: exacto (saldoDisplay ≤ 0.01) o con una miga de redondeo TRAS
  // pago (saldoDisplay ≤ tolerancia y débito − saldo > 0). tolLedger expresa TOLERANCIA_REDONDEO_LOCAL
  // en la moneda del ledger: ~10 en ledger local, ~0 (inerte) en ledger USD. Ver nota del constante.
  const tolLedger = toDisplayCurrency(TOLERANCIA_REDONDEO_LOCAL, monedaPrincipal, null);
  for (const [key, item] of saldosPorFactura) {
    const debitoItem = item.factura?.debitoDisplay ?? 0;
    const huboPago = debitoItem - item.saldoDisplay > 0.01; // se aplicó pago/NC/MBF a esta factura
    const migaTrasPago = huboPago && item.saldoDisplay <= tolLedger;
    if (item.saldoDisplay <= 0.01 || migaTrasPago) saldosPorFactura.delete(key);
  }

  // El saldo inicial NO es una factura: aunque participó del FIFO (Pasada 2/3) para amortizar
  // primero la deuda pre-corte, su residual (si los pagos/NCs no la cubrieron) NO debe figurar
  // como fila de factura en "Por pagar". Se quita del map → su residual fluye al RESIDUO vía
  // (saldoFinal − totalPorFactura), que es donde corresponde la deuda pre-2026 no atribuible.
  saldosPorFactura.delete(`INI:${provId}`);

  // Residuo: deuda global del ledger no atribuible a ninguna factura específica.
  // Aparece cuando hay cobros a cuenta / saldo inicial que suben el saldo global
  // pero el FIFO no los aplica a facturas. Se emite como entry sintética con key
  // "RESIDUO" para que el consumidor pueda mostrarlo como "Saldo a cuenta".
  // El residuo SOLO se emite con saldo final POSITIVO (le debemos): "Por pagar" es deuda
  // pendiente. Un saldo ≤ 0 significa que ya se pagó todo (o de más — ej. pagos sin factura
  // que los respalde) → NO es deuda futura, no se emite. Antes se usaba `Math.abs(saldoFinal)`,
  // que volteaba esos pagos-sin-factura a un fantasma positivo fechado hoy en "Por pagar".
  //
  // Excepción: si TODAS las facturas del proveedor están cubiertas por vinculación directa
  // (MBF), no hay deuda real — la diferencia del saldo global es puramente cambiaria (pagos
  // locales convertidos a moneda extranjera no dan exacto). En ese caso el residuo se fuerza
  // a 0 para no proyectar esa diferencia de TC como "Saldo a cuenta" pendiente. El saldo
  // global (entries) NO se toca: sigue reflejando la diferencia cambiaria en EstadoCuenta.
  const todasCubiertasPorMBF = totalFacturasProveedor > 0 && facturasExcluidasMBF === totalFacturasProveedor;
  const totalPorFactura = [...saldosPorFactura.values()].reduce((acc, item) => acc + item.saldoDisplay, 0);
  const saldoFinal = entriesConSaldo.length > 0 ? entriesConSaldo.at(-1).saldo : 0;
  const residuo =
    todasCubiertasPorMBF || saldoFinal <= 0.01 ? 0 : Math.round((saldoFinal - totalPorFactura) * 100) / 100;
  if (residuo > 0.01) {
    saldosPorFactura.set("RESIDUO", {
      saldoDisplay: residuo,
      factura: {
        origen: "RESIDUO",
        concepto: "Saldo a cuenta",
        referencia: null,
        fecha: new Date().toISOString().slice(0, 10),
      },
    });
  }

  // Enriquecimiento (no afecta el cálculo): marcar entries de pago cuyo movimiento bancario
  // tiene fila MBF. EstadoCuenta lo usa para decidir Desvincular (Sección A, libera factura)
  // vs Eliminar (Sección B, solo caja+banco). Aditivo — ver pendiente 9.9.
  const mbIdsConMBF = new Set((vinculacionesDirectas || []).map((v) => v.movimiento_bancario_id));
  entriesConSaldo.forEach((e) => {
    if (e.movimientoBancarioId && mbIdsConMBF.has(e.movimientoBancarioId)) {
      e.tieneMBF = true;
    }
  });

  return { entries: entriesConSaldo.reverse(), saldosPorFactura };
};
